import { z } from 'zod';
import {
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  MoneySchema,
  pageOf,
  withMeta,
} from '../common';
import { AccountSchema, InvestorSchema, LedgerRowSchema, RevAcctSchema } from '../dataset/core';
import { PfInvestorSchema } from '../dataset/score';
import { StyleDataSchema } from '../dataset/style';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** manage.* (accounts · investors · styleProfile) — Accounts, Investors, Trading Style Profile (SDD §7.4 Manage rows).
 *  `manage.notifications.*` and `manage.billing.*` live in their own families (notifications, billing). */

export const BrokerSchema = z.enum(['tradelocker', 'metaapi', 'ctrader', 'fix']);
export type Broker = z.infer<typeof BrokerSchema>;
export const SyncStateSchema = z.enum(['healthy', 'degraded', 'stale', 'unlinked']);
export const AccountHealthSchema = z
  .object({
    accountId: IdSchema,
    broker: BrokerSchema,
    sync: SyncStateSchema,
    lastSyncAt: IsoDateTimeSchema.nullable(),
    /** Degraded-source banner shows the last-good timestamp (§7.4 inherited baseline). */
    lastGoodAt: IsoDateTimeSchema.nullable(),
    payoutCountdownDays: z.number().int().nullable(),
  })
  .strict();
export const AccountsListSchema = withMeta({
  accounts: z.array(AccountSchema),
  /** REV_ACCTS — per-account review ledger rows the reviewed-sessions views read. */
  reviewAccounts: z.array(RevAcctSchema),
  compositeEquity: MoneySchema,
  health: z.array(AccountHealthSchema),
});
export const LinkAccountInputSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    broker: BrokerSchema,
    /** Broker-specific, non-secret link parameters (server, account number, environment). Secrets never travel through
     *  procedures — the link flow hands off to the broker's own auth (§19). */
    params: z.record(z.string(), z.string()),
    nickname: z.string().min(1).max(40),
  })
  .strict();
export const LinkStateSchema = withMeta({
  linkId: IdSchema,
  broker: BrokerSchema,
  state: z.enum(['pending_verification', 'verified', 'failed']),
  accountId: IdSchema.nullable(),
  message: z.string(),
});
export const QuotaSchema = withMeta({
  used: z.number().int(),
  total: z.number().int(),
  purchasable: z.boolean(),
  /** Slot purchase is pending-webhook: never granted locally (§7.4 Billing row). */
  pendingSlots: z.number().int(),
});

export const InvestorGrantSchema = z
  .object({
    id: IdSchema,
    investor: PfInvestorSchema,
    status: z.enum(['pending', 'active', 'expired', 'revoked']),
    expiresAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
  })
  .strict();
export type InvestorGrant = z.infer<typeof InvestorGrantSchema>;
export const InvestorTermsSchema = z
  .object({
    model: z.enum(['profit_split', 'fixed', 'shareholding', 'hybrid']),
    profitSplitPct: z.number().min(0).max(100).nullable(),
    fixedAmount: MoneySchema.nullable(),
    sharePct: z.number().min(0).max(100).nullable(),
    notes: z.string().max(1000),
  })
  .strict();
export type InvestorTerms = z.infer<typeof InvestorTermsSchema>;
export const InvestorPaymentSchema = z
  .object({
    id: IdSchema,
    grantId: IdSchema,
    amount: MoneySchema,
    currency: z.string(),
    status: z.enum(['pending', 'paid', 'waived', 'disputed']),
    at: IsoDateTimeSchema,
  })
  .strict();
export type InvestorPayment = z.infer<typeof InvestorPaymentSchema>;
export const InvestorsSchema = withMeta({
  /** The investor interest cards shown on the score screen (reference scoreInvestors). */
  interests: z.array(InvestorSchema),
  grants: z.array(InvestorGrantSchema),
});

export const StyleProfileSchema = withMeta({
  data: StyleDataSchema,
  /** Null until the trader completes the style wizard — the state every account starts in, and the reason the
   *  screen shows the wizard rather than an error. */
  profile: z
    .object({
      styleId: z.string().nullable(),
      experienceId: z.string().nullable(),
      sessions: z.array(z.string()),
      instruments: z.array(z.string()),
      rules: z.array(z.object({ id: IdSchema, text: z.string(), on: z.boolean() }).strict()),
      creed: z.string(),
      goals: z.array(z.string()),
      version: z.number().int(),
      updatedAt: IsoDateTimeSchema,
    })
    .strict()
    .nullable(),
});
export const StyleProfileUpdateSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    styleId: z.string().optional(),
    experienceId: z.string().optional(),
    sessions: z.array(z.string()).max(8).optional(),
    instruments: z.array(z.string()).max(24).optional(),
    rules: z
      .array(z.object({ id: IdSchema, text: z.string().max(200), on: z.boolean() }).strict())
      .max(30)
      .optional(),
    creed: z.string().max(500).optional(),
    goals: z.array(z.string().max(200)).max(10).optional(),
  })
  .strict();
export const AutoDetectSchema = withMeta({
  styleId: z.string(),
  experienceId: z.string(),
  sessions: z.array(z.string()),
  instruments: z.array(z.string()),
  confidence: z.number(),
  /** "What this changes" panel. */
  changes: z.array(z.string()),
});

export const manageProcedures = defineProcedures({
  'manage.accounts.list': query('Linked accounts, composite equity, health', z.void(), AccountsListSchema),
  'manage.accounts.link': mutation(
    'Start a broker link flow (4 brokers, §19)',
    LinkAccountInputSchema,
    LinkStateSchema,
  ),
  'manage.accounts.verify': mutation(
    'Verify a pending link — on a live rail this contacts the provider with the given secrets',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        linkId: IdSchema,
        /** WRITE-ONLY: provider secrets (password) for the verification handshake. Used for the provider
         *  call, then enveloped into the credential store — never stored raw, never echoed back, never
         *  logged (crypto.scrub). Absent on the sim rail. */
        secrets: z.record(z.string(), z.string()).optional(),
      })
      .strict(),
    LinkStateSchema,
  ),
  'manage.accounts.health': query(
    'Sync health per account',
    z.void(),
    withMeta({ items: z.array(AccountHealthSchema) }),
  ),
  'manage.accounts.unlink': mutation(
    'Unlink an account',
    z.object({ idempotencyKey: IdempotencyKeySchema, accountId: IdSchema }).strict(),
    AccountsListSchema,
  ),
  'manage.accounts.quota': query('Account slot quota + purchase path', z.void(), QuotaSchema),
  'manage.accounts.ledger': query(
    'Payouts, resets and drawdown events ledger (R-05)',
    CursorInputSchema.optional(),
    pageOf(LedgerRowSchema),
  ),
  'manage.accounts.purchaseSlots': mutation(
    'Purchase extra account slots (pending until the Stripe webhook lands)',
    z.object({ idempotencyKey: IdempotencyKeySchema, slots: z.number().int().min(1).max(10) }).strict(),
    QuotaSchema,
  ),

  'manage.investors.list': query('Investor interests + grants', z.void(), InvestorsSchema),
  'manage.investors.grant': mutation(
    'Create a grant with expiry',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        investorName: z.string().min(1).max(80),
        email: z.email(),
        expiresAt: IsoDateTimeSchema.nullable(),
      })
      .strict(),
    // The created grant, plus its link token — the ONLY time the plaintext exists outside the investor's inbox.
    // Only the keyed hash is stored, so this response is the trader's single opportunity to copy the link, and the
    // list view returns `InvestorGrantSchema` without it because there is nothing left to return.
    //
    // Nullable for one case: a replay of an idempotency key already used. The grant is returned, because the caller
    // is entitled to know their request succeeded, and the token is null, because it cannot be recovered from a hash
    // and inventing a second working link for the same grant would be worse than saying so.
    InvestorGrantSchema.extend({ grantToken: z.string().nullable() }),
  ),
  'manage.investors.revoke': mutation(
    'Revoke a grant',
    z.object({ idempotencyKey: IdempotencyKeySchema, grantId: IdSchema }).strict(),
    InvestorGrantSchema,
  ),
  'manage.investors.terms': query(
    'Terms of a grant',
    z.object({ grantId: IdSchema }).strict(),
    withMeta({ grantId: IdSchema, terms: InvestorTermsSchema }),
  ),
  'manage.investors.updateTerms': mutation(
    'Edit terms (profit-split / fixed / shareholding / hybrid)',
    z
      .object({ idempotencyKey: IdempotencyKeySchema, grantId: IdSchema, terms: InvestorTermsSchema })
      .strict(),
    withMeta({ grantId: IdSchema, terms: InvestorTermsSchema }),
  ),
  'manage.investors.payments': query(
    'Payment ledger',
    CursorInputSchema.extend({ grantId: IdSchema.optional() }).optional(),
    pageOf(InvestorPaymentSchema),
  ),
  'manage.investors.recordPayment': mutation(
    'Record a payment state',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        grantId: IdSchema,
        amount: MoneySchema,
        currency: z.string().length(3),
        status: z.enum(['pending', 'paid', 'waived', 'disputed']),
      })
      .strict(),
    InvestorPaymentSchema,
  ),
  'manage.investors.messageAll': mutation(
    'Message all investor viewers (R-10)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        subject: z.string().min(1).max(120),
        body: z.string().min(1).max(4000),
      })
      .strict(),
    withMeta({ sent: z.number().int() }),
  ),

  'manage.styleProfile.get': query(
    'Trading style profile + wizard vocabularies',
    z.void(),
    StyleProfileSchema,
  ),
  'manage.styleProfile.update': mutation(
    'Update the profile (versioned)',
    StyleProfileUpdateSchema,
    StyleProfileSchema,
  ),
  'manage.styleProfile.autoDetect': mutation(
    'Auto-detect style from history',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    AutoDetectSchema,
  ),
});
export type ManageFamilyClient = RouterShape<typeof manageProcedures>;

export const manageFamily = defineFamily({
  name: 'manage',
  title: 'Manage',
  screens: ['Accounts', 'Investors', 'Trading Style Profile'],
  procedures: manageProcedures,
  binding: 'simulated',
});
